import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFormatter, useTranslations } from "use-intl";
import {
  createMcpToken,
  type CreatedMcpToken,
  listMcpTokens,
  listProjects,
  MCP_ENDPOINT,
  type McpTokenExpiryDays,
  type McpTokenItem,
  type FlowTraceProjectItem,
  revokeMcpToken,
} from "../lib/api";
import { useAppLocale } from "../i18n";
import { AuthUserMenu } from "./AuthUserMenu";
import { trackFlowTraceSelf } from "./PublicFlowTraceAnalytics";
import { Brand } from "./Brand";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SiteFooter } from "./SiteFooter";

const EXPIRY_OPTIONS: McpTokenExpiryDays[] = [7, 30, 90];
const CODEX_ADD_COMMAND = `codex mcp add flowtrace --url ${MCP_ENDPOINT} --bearer-token-env-var FLOWTRACE_MCP_TOKEN`;
// 単引用符で囲み、トークンの値ではなく変数名を .mcp.json に保存させる
const CLAUDE_ADD_COMMAND = `claude mcp add --transport http --scope project flowtrace ${MCP_ENDPOINT} --header 'Authorization: Bearer \${FLOWTRACE_MCP_TOKEN}'`;
const TOKEN_ENV_COMMAND =
  "printf 'FlowTrace MCP token: '; read -s FLOWTRACE_MCP_TOKEN; printf '\\n'; export FLOWTRACE_MCP_TOKEN";

type ListStatus = "error" | "loading" | "ready";
type TokenStatus = "active" | "expired" | "revoked";
type CopyTarget = "claude" | "codex" | "endpoint" | "prompt" | "token";

const tokenStatus = (item: McpTokenItem): TokenStatus => {
  if (item.revokedAt) return "revoked";
  if (item.expiresAt && new Date(item.expiresAt).getTime() <= Date.now()) {
    return "expired";
  }
  return "active";
};

export const McpAccessPage = () => {
  const format = useFormatter();
  const t = useTranslations("mcpAccess");
  const { timeZone } = useAppLocale();
  const [tokens, setTokens] = useState<McpTokenItem[]>([]);
  const [projects, setProjects] = useState<FlowTraceProjectItem[]>([]);
  const [selectedAppId, setSelectedAppId] = useState("");
  const [listStatus, setListStatus] = useState<ListStatus>("loading");
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] =
    useState<McpTokenExpiryDays>(30);
  const [creating, setCreating] = useState(false);
  const [createFailed, setCreateFailed] = useState(false);
  const [revokeFailed, setRevokeFailed] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<CreatedMcpToken | null>(null);
  const [copied, setCopied] = useState<CopyTarget | null>(null);
  const [copyFailed, setCopyFailed] = useState<CopyTarget | null>(null);

  const loadTokens = useCallback(async () => {
    setListStatus("loading");
    try {
      const [nextTokens, nextProjects] = await Promise.all([
        listMcpTokens(),
        listProjects(),
      ]);
      const activeProjects = nextProjects.filter(
        (project) => project.status === "active",
      );
      setTokens(nextTokens);
      setProjects(activeProjects);
      setSelectedAppId((current) =>
        activeProjects.some((project) => project.appId === current)
          ? current
          : activeProjects[0]?.appId ?? "",
      );
      setListStatus("ready");
    } catch {
      setListStatus("error");
    }
  }, []);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  const copyValue = async (value: string, target: CopyTarget) => {
    setCopied(null);
    setCopyFailed(null);
    try {
      if (!navigator.clipboard) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(value);
      setCopied(target);
      trackFlowTraceSelf("mcp.copied", { target });
    } catch {
      setCopyFailed(target);
    }
  };

  const createToken = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName || !selectedAppId || revealed) return;

    setCreating(true);
    setCreateFailed(false);
    try {
      const created = await createMcpToken({
        appIds: [selectedAppId],
        expiresInDays,
        name: trimmedName,
      });
      setTokens((current) => [
        created.item,
        ...current.filter((item) => item.id !== created.item.id),
      ]);
      setRevealed(created);
      trackFlowTraceSelf("mcp.token.created", { expiresInDays });
      setName("");
      setCopied(null);
      setCopyFailed(null);
    } catch {
      trackFlowTraceSelf("mcp.token.create_failed");
      setCreateFailed(true);
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async (item: McpTokenItem) => {
    if (!window.confirm(t("revokeConfirm", { name: item.name }))) return;
    setRevokingId(item.id);
    setRevokeFailed(false);
    try {
      await revokeMcpToken(item.id);
      const now = new Date().toISOString();
      setTokens((current) =>
        current.map((token) =>
          token.id === item.id
            ? { ...token, revokedAt: now, updatedAt: now }
            : token,
        ),
      );
    } catch {
      setRevokeFailed(true);
    } finally {
      setRevokingId(null);
    }
  };

  const formatDate = (value: string) =>
    format.dateTime(new Date(value), {
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      month: "short",
      timeZone,
      year: "numeric",
    });

  return (
    <div className="app-shell mcp-access-page">
      <header className="topbar">
        <Brand />
        <div className="topbar-actions">
          <LanguageSwitcher />
          <AuthUserMenu />
          <Link className="text-link" to="/">{t("back")}</Link>
        </div>
      </header>

      <main>
        <section className="mcp-access-hero">
          <div>
            <p className="eyebrow">{t("eyebrow")}</p>
            <h1>{t("title")}</h1>
            <p>{t("description")}</p>
          </div>
          <div className="mcp-endpoint-card">
            <span>{t("endpointLabel")}</span>
            <code>{MCP_ENDPOINT}</code>
            <button
              className="button button-dark"
              onClick={() => void copyValue(MCP_ENDPOINT, "endpoint")}
              type="button"
            >
              {copied === "endpoint" ? t("copied") : t("copyEndpoint")}
            </button>
            {copyFailed === "endpoint" && (
              <small role="alert">{t("copyFailed")}</small>
            )}
          </div>
        </section>

        <section className="mcp-guardrail-grid" aria-label={t("accessModel")}>
          <article>
            <span aria-hidden="true">01</span>
            <h2>{t("readOnlyTitle")}</h2>
            <p>{t("readOnlyDescription")}</p>
          </article>
          <article>
            <span aria-hidden="true">02</span>
            <h2>{t("summaryOnlyTitle")}</h2>
            <p>{t("summaryOnlyDescription")}</p>
          </article>
          <article>
            <span aria-hidden="true">03</span>
            <h2>{t("scopeTitle")}</h2>
            <p>{t("scopeDescription")}</p>
          </article>
        </section>

        <section className="mcp-connection-panel">
          <div className="mcp-connection-heading">
            <div>
              <p className="eyebrow">{t("connectionEyebrow")}</p>
              <h2>{t("connectionTitle")}</h2>
            </div>
            <p>{t("connectionDescription")}</p>
          </div>
          <div className="mcp-connection-grid">
            <ol className="mcp-connection-steps">
              <li>
                <span>1</span>
                <div>
                  <strong>{t("envStepTitle")}</strong>
                  <p>{t("envStepDescription")}</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>{t("configStepTitle")}</strong>
                  <p>{t("configStepDescription")}</p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>{t("restartStepTitle")}</strong>
                  <p>{t("restartStepDescription")}</p>
                </div>
              </li>
            </ol>
            <div className="mcp-config-example">
              <div>
                <span>{t("envCommandLabel")}</span>
                <code>{TOKEN_ENV_COMMAND}</code>
              </div>
              {([
                ["codex", t("codexCommandLabel"), CODEX_ADD_COMMAND],
                ["claude", t("claudeCommandLabel"), CLAUDE_ADD_COMMAND],
              ] as const).map(([target, label, command]) => (
                <div key={target}>
                  <div className="mcp-config-label">
                    <span>{label}</span>
                    <button
                      onClick={() => void copyValue(command, target)}
                      type="button"
                    >
                      {copied === target ? t("copied") : t("copyCommand")}
                    </button>
                  </div>
                  <pre><code>{command}</code></pre>
                </div>
              ))}
              <small>{t("configNoPlaintext")}</small>
              {(copyFailed === "codex" || copyFailed === "claude") && (
                <small className="mcp-copy-error" role="alert">
                  {t("copyFailed")}
                </small>
              )}
            </div>
          </div>
          <div className="mcp-first-check">
            <div>
              <strong>{t("firstCheckTitle")}</strong>
              <p>{t("firstCheckDescription")}</p>
            </div>
            <div className="mcp-check-flow" aria-label={t("firstCheckFlowLabel")}>
              <code>
                find_flowtrace_friction(appId={selectedAppId || "projectId"})
              </code>
              <span aria-hidden="true">→</span>
              <code>get_flowtrace_session_journey(journeyRef)</code>
              <span aria-hidden="true">→</span>
              <code>{t("firstCheckFix")}</code>
            </div>
            <div className="mcp-config-label">
              <span>{t("promptLabel")}</span>
              <button
                onClick={() =>
                  void copyValue(
                    t("promptExample", { appId: selectedAppId || "projectId" }),
                    "prompt",
                  )}
                type="button"
              >
                {copied === "prompt" ? t("copied") : t("copyPrompt")}
              </button>
            </div>
            <pre><code>{t("promptExample", { appId: selectedAppId || "projectId" })}</code></pre>
            {copyFailed === "prompt" && (
              <small className="mcp-copy-error" role="alert">
                {t("copyFailed")}
              </small>
            )}
          </div>
        </section>

        <div className="mcp-access-grid">
          <section className="mcp-panel mcp-create-panel">
            <div className="mcp-panel-heading">
              <div>
                <p className="eyebrow">{t("issueEyebrow")}</p>
                <h2>{t("issueTitle")}</h2>
              </div>
              <span className="privacy-chip">{t("oneTimeBadge")}</span>
            </div>
            <p className="mcp-panel-description">{t("issueDescription")}</p>

            {revealed && (
              <div
                aria-live="polite"
                className="mcp-token-reveal"
                data-flowtrace-block
              >
                <div>
                  <strong>{t("tokenReady")}</strong>
                  <span>{t("tokenReadyFor", { name: revealed.item.name })}</span>
                </div>
                <p>{t("oneTimeWarning")}</p>
                <code>{revealed.token}</code>
                <div className="mcp-reveal-actions">
                  <button
                    className="button button-primary"
                    onClick={() => void copyValue(revealed.token, "token")}
                    type="button"
                  >
                    {copied === "token" ? t("copied") : t("copyToken")}
                  </button>
                  <button
                    className="button mcp-secondary-button"
                    onClick={() => {
                      setRevealed(null);
                      setCopied(null);
                      setCopyFailed(null);
                    }}
                    type="button"
                  >
                    {t("savedToken")}
                  </button>
                </div>
                {copyFailed === "token" && (
                  <small role="alert">{t("copyFailed")}</small>
                )}
                <small>{t("cannotRecover")}</small>
              </div>
            )}

            <form className="mcp-token-form" onSubmit={createToken}>
              <label>
                <span>{t("nameLabel")}</span>
                <input
                  autoComplete="off"
                  disabled={creating || Boolean(revealed)}
                  maxLength={80}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t("namePlaceholder")}
                  required
                  value={name}
                />
                <small>{t("nameHint")}</small>
              </label>
              <label>
                <span>{t("expiryLabel")}</span>
                <select
                  disabled={creating || Boolean(revealed)}
                  onChange={(event) =>
                    setExpiresInDays(
                      Number(event.target.value) as McpTokenExpiryDays,
                    )
                  }
                  value={expiresInDays}
                >
                  {EXPIRY_OPTIONS.map((days) => (
                    <option key={days} value={days}>
                      {t("expiryDays", { days })}
                    </option>
                  ))}
                </select>
                <small>{t("expiryHint")}</small>
              </label>
              <label>
                <span>{t("appLabel")}</span>
                <select
                  disabled={creating || Boolean(revealed) || projects.length === 0}
                  onChange={(event) => setSelectedAppId(event.target.value)}
                  required
                  value={selectedAppId}
                >
                  {projects.map((project) => (
                    <option key={project.appId} value={project.appId}>
                      {project.name}
                    </option>
                  ))}
                </select>
                <small>
                  {projects.length === 0
                    ? t("noProjects")
                    : t("appFixedHint")}
                </small>
              </label>
              <button
                className="button button-primary"
                data-flowtrace-id="mcp.token.create"
                disabled={
                  creating || Boolean(revealed) || !name.trim() || !selectedAppId
                }
                type="submit"
              >
                {creating ? t("issuing") : t("issueAction")}
              </button>
              {revealed && <small>{t("saveBeforeNext")}</small>}
              {createFailed && (
                <p className="mcp-action-error" role="alert">
                  {t("issueFailed")}
                </p>
              )}
            </form>
          </section>

          <section className="mcp-panel mcp-list-panel">
            <div className="mcp-panel-heading">
              <div>
                <p className="eyebrow">{t("tokensEyebrow")}</p>
                <h2>{t("tokensTitle")}</h2>
              </div>
              {listStatus === "ready" && (
                <span className="mcp-token-count">
                  {t("tokenCount", { count: tokens.length })}
                </span>
              )}
            </div>
            <p className="mcp-panel-description">{t("tokensDescription")}</p>

            {listStatus === "loading" && (
              <div className="mcp-list-state">{t("loading")}</div>
            )}
            {listStatus === "error" && (
              <div className="mcp-list-state mcp-list-error" role="alert">
                <span>{t("loadFailed")}</span>
                <button
                  className="text-link mcp-inline-action"
                  onClick={() => void loadTokens()}
                  type="button"
                >
                  {t("retry")}
                </button>
              </div>
            )}
            {listStatus === "ready" && tokens.length === 0 && (
              <div className="mcp-list-state">{t("emptyTokens")}</div>
            )}
            {revokeFailed && (
              <p className="mcp-action-error" role="alert">
                {t("revokeFailed")}
              </p>
            )}
            {listStatus === "ready" && tokens.length > 0 && (
              <div className="mcp-token-list" data-flowtrace-block>
                {tokens.map((item) => {
                  const status = tokenStatus(item);
                  return (
                    <article className="mcp-token-item" key={item.id}>
                      <header>
                        <div>
                          <h3>{item.name}</h3>
                          <code>
                            {item.tokenPrefix}••••{item.tokenSuffix}
                          </code>
                        </div>
                        <span className={`mcp-status mcp-status-${status}`}>
                          {t(`status.${status}`)}
                        </span>
                      </header>
                      <dl>
                        <div>
                          <dt>{t("apps")}</dt>
                          <dd>
                            {item.appIds
                              .map(
                                (appId) =>
                                  projects.find(
                                    (project) => project.appId === appId,
                                  )?.name ?? appId,
                              )
                              .join(", ")}
                          </dd>
                        </div>
                        <div>
                          <dt>{t("createdAt")}</dt>
                          <dd>{formatDate(item.createdAt)}</dd>
                        </div>
                        <div>
                          <dt>{t("expiresAt")}</dt>
                          <dd>
                            {item.expiresAt
                              ? formatDate(item.expiresAt)
                              : t("noExpiry")}
                          </dd>
                        </div>
                        <div>
                          <dt>{t("lastUsedAt")}</dt>
                          <dd>
                            {item.lastUsedAt
                              ? formatDate(item.lastUsedAt)
                              : t("neverUsed")}
                          </dd>
                        </div>
                      </dl>
                      {status === "active" && (
                        <button
                          className="mcp-revoke-button"
                          disabled={revokingId === item.id}
                          onClick={() => void revokeToken(item)}
                          type="button"
                        >
                          {revokingId === item.id
                            ? t("revoking")
                            : t("revoke")}
                        </button>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};
